import { type ChatMessagePayload, type LegalSourcePayload, type LegalSourcePayloadBag } from '#app/services/ai/types.ts'
import { type LegalSourceLegacyPayloadKey } from './source-contract.ts'

export const MAX_CITATION_GROUNDING_TEXT_LENGTH = 6000
export const MAX_EXACT_SOURCE_CITATION_GROUNDING_TEXT_LENGTH = 20_000
export const MAX_PERSISTED_CITATION_GROUNDING_CHARACTERS = 120_000

export type LegalSourcePayloadOwner = {
	id: string
	legacyPayloadKey?: LegalSourceLegacyPayloadKey
	metrics?: {
		key: string
	}
	merge?: {
		dedupeKey?: (payload: any) => string | number
		documentKey?: (payload: any) => string
	}
}

export type LegalSourceGroundingBudgetStats = {
	originalCharacters: number
	persistedCharacters: number
	truncatedSources: number
	omittedSourceTexts: number
	duplicateTextsRemoved: number
	sourceTextCount: number
	persistedSourceTexts: number
}

export function normalizeCitationGroundingText(
	text: string | null | undefined,
	maxLength = MAX_CITATION_GROUNDING_TEXT_LENGTH,
) {
	return (text ?? '')
		.trim()
		.replace(/\n{3,}/g, '\n\n')
		.slice(0, maxLength)
}

export function addCitationGroundingText<T extends object>(
	payload: T,
	text: string | null | undefined,
	options: { maxLength?: number } = {},
) {
	const citationGroundingText = normalizeCitationGroundingText(text, options.maxLength)
	return citationGroundingText ? { ...payload, citationGroundingText } : payload
}

function defaultDedupeKey(payload: LegalSourcePayload) {
	let id = payload.idDocument ?? 'no-id'
	return payload.id + '-' + id
}

export function mergeLegalSourcePayloads(
	source: LegalSourcePayloadOwner,
	payloads: Array<LegalSourcePayload | null | undefined>,
) {
	const getKey = source.merge?.dedupeKey ?? defaultDedupeKey
	const seen = new Set<string | number>()
	const result: LegalSourcePayload[] = []

	for (const payload of payloads) {
		if (!payload) continue
		const key = getKey(payload)
		if (seen.has(key)) continue
		seen.add(key)
		result.push(payload)
	}

	return result
}

export function getLegalSourcePayloads(payloads: ChatMessagePayload, source: LegalSourcePayloadOwner) {
	return mergeLegalSourcePayloads(source, [
		...(payloads.legalSources?.[source.id] ?? []),
		...(source.legacyPayloadKey ? (payloads[source.legacyPayloadKey] ?? []) : []),
	] as LegalSourcePayload[])
}

export function getLegalSourceMetricKey(source: LegalSourcePayloadOwner) {
	return source.metrics?.key ?? source.legacyPayloadKey ?? source.id
}

export function getLegalSourcePayloadCounts(
	sources: readonly LegalSourcePayloadOwner[],
	payloads: ChatMessagePayload | null | undefined,
) {
	const safePayloads: ChatMessagePayload = payloads ?? {}
	const bySourceId: Record<string, number> = {}
	const byMetricKey: Record<string, number> = {}
	const byLegacyPayloadKey: Partial<Record<LegalSourceLegacyPayloadKey, number>> = {}
	let total = 0

	for (const source of sources) {
		const count = getLegalSourcePayloads(safePayloads, source).length
		const metricKey = getLegalSourceMetricKey(source)
		bySourceId[source.id] = count
		byMetricKey[metricKey] = count
		if (source.legacyPayloadKey) {
			byLegacyPayloadKey[source.legacyPayloadKey] = count
		}
		total += count
	}

	return { bySourceId, byMetricKey, byLegacyPayloadKey, total }
}

export function getLegalSourcePayloadCountLogFields(
	sources: readonly LegalSourcePayloadOwner[],
	payloads: ChatMessagePayload | null | undefined,
	options: { prefix?: string } = {},
) {
	const counts = getLegalSourcePayloadCounts(sources, payloads)
	return Object.fromEntries(
		sources.map(source => [
			`${options.prefix ?? ''}${getLegalSourceMetricKey(source)}_payload_count`,
			counts.bySourceId[source.id] ?? 0,
		]),
	) as Record<string, number>
}

export function findLegalSourcePayload(
	payloads: ChatMessagePayload,
	documentId: string,
	source: LegalSourcePayloadOwner,
) {
	let result = getLegalSourcePayloads(payloads, source).find(payload => payload.idDocument === documentId) ?? null
	return result
}

export function mergeLegalSourcePayloadBags(
	sources: readonly LegalSourcePayloadOwner[],
	...bags: Array<LegalSourcePayloadBag | null | undefined>
) {
	const result: LegalSourcePayloadBag = {}
	const sourceById = new Map(sources.map(source => [source.id, source]))

	for (const bag of bags) {
		if (!bag) continue
		for (const [sourceId, payloads] of Object.entries(bag)) {
			const source = sourceById.get(sourceId) ?? { id: sourceId }
			result[sourceId] = mergeLegalSourcePayloads(source, [...(result[sourceId] ?? []), ...payloads])
		}
	}

	return result
}

export function buildLegalSourcePayloadBagFromLegacyPayloads(
	sources: readonly LegalSourcePayloadOwner[],
	payloads: Pick<ChatMessagePayload, LegalSourceLegacyPayloadKey>,
	existingBag?: LegalSourcePayloadBag | null,
) {
	const sourcePayloads = mergeLegalSourcePayloadBags(sources, existingBag)

	for (const source of sources) {
		if (!source.legacyPayloadKey) continue
		const legacyPayloads = payloads[source.legacyPayloadKey] ?? []
		sourcePayloads[source.id] = mergeLegalSourcePayloads(source, [
			...(sourcePayloads[source.id] ?? []),
			...legacyPayloads,
		] as LegalSourcePayload[])
	}

	return sourcePayloads
}

export function addPayloadToLegalSourceBag(
	bag: LegalSourcePayloadBag,
	source: LegalSourcePayloadOwner,
	payload: LegalSourcePayload,
) {
	bag[source.id] = mergeLegalSourcePayloads(source, [...(bag[source.id] ?? []), payload])
}

function getGroundingText(payload: LegalSourcePayload) {
	return normalizeCitationGroundingText(payload.citationGroundingText, Number.MAX_SAFE_INTEGER)
}

function stripGroundingStorageFields(payload: LegalSourcePayload): LegalSourcePayload {
	const {
		citationGroundingText: _citationGroundingText,
		citationGroundingPriority: _citationGroundingPriority,
		...metadata
	} = payload
	return metadata as LegalSourcePayload
}

function getCanonicalDocumentKey(source: LegalSourcePayloadOwner, payload: LegalSourcePayload) {
	return String(
		source.merge?.documentKey?.(payload) ?? payload.idDocument ?? source.merge?.dedupeKey?.(payload) ?? payload.id,
	)
}

function mergePayloadCopies(
	source: LegalSourcePayloadOwner,
	payloadLists: LegalSourcePayload[][],
	stats: LegalSourceGroundingBudgetStats,
) {
	const getKey = source.merge?.dedupeKey ?? defaultDedupeKey
	const merged = new Map<string | number, LegalSourcePayload>()
	for (const payload of payloadLists.flat()) {
		const text = getGroundingText(payload)
		if (text) {
			stats.originalCharacters += text.length
			stats.sourceTextCount += 1
		}
		const key = getKey(payload)
		const existing = merged.get(key)
		if (!existing) {
			merged.set(key, { ...payload })
			continue
		}
		if (text) {
			stats.duplicateTextsRemoved += 1
			stats.omittedSourceTexts += 1
		}
		const existingText = getGroundingText(existing)
		const shouldPreferPayloadText = Boolean(text) && (!existingText || payload.citationGroundingPriority === 'exact')
		merged.set(key, {
			...existing,
			...(shouldPreferPayloadText ? { citationGroundingText: text } : {}),
			...(payload.citationGroundingPriority === 'exact' ? { citationGroundingPriority: 'exact' as const } : {}),
		})
	}
	return [...merged.values()]
}

export function enforceLegalSourceGroundingBudget(
	sources: readonly LegalSourcePayloadOwner[],
	payloads: ChatMessagePayload,
	options: { maxCharacters?: number; prioritizedDocumentIds?: Iterable<string> } = {},
): { payloads: ChatMessagePayload; stats: LegalSourceGroundingBudgetStats } {
	const stats: LegalSourceGroundingBudgetStats = {
		originalCharacters: 0,
		persistedCharacters: 0,
		truncatedSources: 0,
		omittedSourceTexts: 0,
		duplicateTextsRemoved: 0,
		sourceTextCount: 0,
		persistedSourceTexts: 0,
	}
	const sourceById = new Map(sources.map(source => [source.id, source]))
	const sourceIds = [
		...sources.map(source => source.id),
		...Object.keys(payloads.legalSources ?? {}).filter(sourceId => !sourceById.has(sourceId)),
	]
	const canonicalBag: LegalSourcePayloadBag = {}
	const prioritizedDocumentIds = new Set(options.prioritizedDocumentIds ?? [])
	const candidates: Array<{
		payload: LegalSourcePayload
		source: LegalSourcePayloadOwner
		documentKey: string
		required: boolean
		order: number
	}> = []
	let order = 0

	for (const sourceId of sourceIds) {
		const source = sourceById.get(sourceId) ?? { id: sourceId }
		const canonical = (payloads.legalSources?.[sourceId] ?? []) as LegalSourcePayload[]
		const legacy = source.legacyPayloadKey ? ((payloads[source.legacyPayloadKey] ?? []) as LegalSourcePayload[]) : []
		const merged = mergePayloadCopies(source, [canonical, legacy], stats)
		if (merged.length === 0) continue
		canonicalBag[sourceId] = merged
		const requiredIds = new Set([
			...(source.legacyPayloadKey ? (payloads.meta?.requiredLegalSourceIds?.[source.legacyPayloadKey] ?? []) : []),
			...(payloads.meta?.requiredLegalSourceIdsBySource?.[sourceId] ?? []),
		])
		for (const payload of merged) {
			if (!getGroundingText(payload)) continue
			const documentKey = getCanonicalDocumentKey(source, payload)
			candidates.push({
				payload,
				source,
				documentKey,
				required:
					requiredIds.has(String(payload.idDocument)) ||
					requiredIds.has(documentKey) ||
					prioritizedDocumentIds.has(String(payload.idDocument)) ||
					prioritizedDocumentIds.has(documentKey),
				order: order++,
			})
		}
	}

	candidates.sort((a, b) => {
		const priorityA = a.payload.citationGroundingPriority === 'exact' ? 0 : a.required ? 1 : 2
		const priorityB = b.payload.citationGroundingPriority === 'exact' ? 0 : b.required ? 1 : 2
		return priorityA - priorityB || a.order - b.order
	})

	let remaining = options.maxCharacters ?? MAX_PERSISTED_CITATION_GROUNDING_CHARACTERS
	const groundedDocuments = new Map<string, Set<string>>()
	for (const candidate of candidates) {
		const originalText = getGroundingText(candidate.payload)
		const uniqueKey = `${candidate.source.id}:${candidate.documentKey}`
		const storedTexts = groundedDocuments.get(uniqueKey) ?? new Set<string>()
		// Different chunks of one law can support different claims. Only identical
		// excerpts are duplicates; document identity alone must not discard evidence.
		if (storedTexts.has(originalText)) {
			delete candidate.payload.citationGroundingText
			stats.duplicateTextsRemoved += 1
			stats.omittedSourceTexts += 1
			continue
		}
		storedTexts.add(originalText)
		groundedDocuments.set(uniqueKey, storedTexts)
		const perSourceLimit =
			candidate.payload.citationGroundingPriority === 'exact'
				? MAX_EXACT_SOURCE_CITATION_GROUNDING_TEXT_LENGTH
				: MAX_CITATION_GROUNDING_TEXT_LENGTH
		const cappedText = originalText.slice(0, perSourceLimit)
		const persistedText = cappedText.slice(0, Math.max(0, remaining))
		if (persistedText) {
			candidate.payload.citationGroundingText = persistedText
			stats.persistedCharacters += persistedText.length
			stats.persistedSourceTexts += 1
			remaining -= persistedText.length
		} else {
			delete candidate.payload.citationGroundingText
			stats.omittedSourceTexts += 1
		}
		if (persistedText.length < originalText.length) stats.truncatedSources += 1
	}

	for (const sourcePayloads of Object.values(canonicalBag)) {
		for (const payload of sourcePayloads) delete payload.citationGroundingPriority
	}

	const result: ChatMessagePayload = {
		...payloads,
		legalSources: Object.keys(canonicalBag).length > 0 ? canonicalBag : undefined,
	}
	for (const source of sources) {
		if (!source.legacyPayloadKey) continue
		const legacyPayloads = mergeLegalSourcePayloads(
			source,
			(payloads[source.legacyPayloadKey] ?? []) as LegalSourcePayload[],
		).map(stripGroundingStorageFields)
		;(result[source.legacyPayloadKey] as LegalSourcePayload[] | undefined) =
			legacyPayloads.length > 0 ? legacyPayloads : undefined
	}

	return { payloads: result, stats }
}
